import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, ElementRef, Renderer2, ViewChild, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { IonButton, IonicModule } from "@ionic/angular";
import { TranslateModule } from "@ngx-translate/core";
import { Subject, takeUntil } from "rxjs";

import { Ticket } from "@sports-pages/ticket/base/ticket-base.component";
import { TicketMethods } from "@sports-utils/index";

// Models
import { LicenseTypes } from "@models/MasterData";
import { TicketModeEnum } from "@ticket/models";

// Pipes
import { SportsFormatOddPipe } from "@sports-pipes/sports.format-odd.pipe";
import { SportsParseStakeMoney } from "@sports-pipes/sports.parse-stake-money.pipe";

// Directives
import { DirectivesModule } from "@directives/directives.module";

// Components
import { CodereGridFilterComponent } from "@sports-components/index";
import { TicketBetbuilderComponent } from "@sports-pages/ticket/components/ticket-betbuilder/ticket-betbuilder.component";
import { TicketBonusComponent } from "@sports-pages/ticket/components/ticket-bonus/ticket-bonus";
import { TicketFilterComponent } from "@sports-pages/ticket/components/ticket-filter/ticket-filter";
import { TicketSelectionComponent } from "@sports-pages/ticket/components/ticket-selection/ticket-selection";
import { SBBetslipStakeComponent } from "@sports-pages/ticket/components/ticket-stake-input/ticket-stake-input.component";
import { LobbyOutComponent } from "../../../../../shared/components/casino/lobby/lobby-out/lobby-out";
import { BetslipTicketConfirmedComponent } from "../../components/ticket-confirmed/ticket-confirmed.component";

@Component({
  selector: "ticket",
  templateUrl: "./ticket.html",
  styleUrls: ["./ticket.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    id: "sb-ticket",
  },
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    TranslateModule,
    SportsParseStakeMoney,
    SportsFormatOddPipe,
    TicketBonusComponent,
    TicketSelectionComponent,
    LobbyOutComponent,
    TicketFilterComponent,
    TicketBetbuilderComponent,
    DirectivesModule,
    CodereGridFilterComponent,
    SBBetslipStakeComponent,
    BetslipTicketConfirmedComponent,
  ],
})
export class TicketComponent extends Ticket {
  @ViewChild("selections") selections: ElementRef;
  @ViewChild("header") header: ElementRef;
  @ViewChild("eliminar_todo") botonEliminarTodo: IonButton;
  confirmationExpanded: boolean = true;
  mode: string = TicketModeEnum.TICKET_EXPANDED;
  /// Stake's input
  declare stakeInput;
  stakeInputUpdate = new Subject<string>();
  swipeActive: boolean;
  hideGames: boolean = false;
  totalOdds: number = 0;
  totalOddsEnabled: boolean = false;
  numberOfSelections: number = 0;
  filterDisabled: boolean = false;
  /// Stake's input
  public confirmationMessage: string;
  renderer = inject(Renderer2);

  constructor() {
    super();

    this.ticket$.pipe(takeUntil(this.unsubscribe$)).subscribe((_ticket$) => {
      this.isMultiSelection();
      this.isConfirmed();
      this.isPending();
      this.setCurrencySign();
      this.filterDisabled = !!_ticket$?.FreebetInstance;
      this.totalOdds = 0;
      this.totalOddsEnabled = this.globalVars.FEATURES.SPO_totalOddsFeatureFlagEnabled
        ? this.globalVars.FEATURES.SPO_totalOddsFeatureFlagEnabled
        : false;
      this.numberOfSelections = this.getNumberOfSelections(this.ticket);
    });
  }

  ngAfterViewInit() {
    this.setCurrencySign();
    this.hasActiveFreebet();
    super.getLastUpdate();
  }

  closeTicketFull() {
    this.mobileBehavior.closeTicketFull();
  }

  private isMultiSelection() {
    if (this.ticket !== undefined) {
      this.mode =
        this.ticket.BetType === 2 ? TicketModeEnum.TICKET_EXPANDED_MULTISELECTION : TicketModeEnum.TICKET_EXPANDED;
    } else {
      this.mode = TicketModeEnum.TICKET_EXPANDED;
    }
  }

  getTicketIsConfirmed(ticket: any): boolean {
    if (ticket !== undefined && ticket !== null) return TicketMethods.getIsConfirmed(ticket);
    else return false;
  }

  getMaxHeight(ticket) {
    if (this.getTicketIsConfirmed(ticket) && !this.isDesktop) {
      const hasUpperClock =
        (this.globalVars.licenseType === LicenseTypes.Colombia && this.globalVars.user.logged) ||
        this.globalVars.licenseType === LicenseTypes.ArgentinaCaba ||
        this.globalVars.licenseType === LicenseTypes.ArgentinaMendoza;
      return `calc(100vh - 66px - ${hasUpperClock ? "76px" : "51px"} - 130px - var(--ion-safe-area-top, 0))`;
    }
  }

  private isPending() {
    if (
      !!this.ticket &&
      TicketMethods.getIsPending(this.ticket) &&
      (this.ticket.OveraskInfo.Status !== "OK" || !this.isDesktop)
    ) {
      this.confirmationExpanded = false;
      this.mode = TicketModeEnum.TICKET_CONFIRMATION_COLLAPSED;
      return;
    } else if (!!this.ticket && this.ticket.OveraskInfo.Status !== "OK" && this.ticket.OveraskInfo.Status === 3) {
      this.confirmationExpanded = false;
      this.mode = TicketModeEnum.TICKET_OVERASK_REJECTED;
    }
  }

  public getNumberOfSelections(data: any) {
    if (data !== undefined && data !== null) return TicketMethods.getNumberOfSelections(data);
    else return undefined;
  }

  public scrollTo(selectionPosition: any) {
    if (this.isDesktop) return;
    setTimeout(() => {
      this.selections.nativeElement.scroll({
        top: (TicketMethods.getSelectedLine(this.ticket).ItemId - 1) * 81,
        left: 0,
        behavior: "smooth",
      });
    }, 500);
  }

  inputChange(value) {
    const num = value.replaceAll(".", ",");
    return Number(num);
  }

  callHasErrors(ticket: any) {
    return TicketMethods.getHasErrors(ticket);
  }
}
